type TranslationEntry = string | Record<string, string>;

type TranslationDictionary = {
  [langCode: string]: {
    [key: string]: TranslationEntry;
  };
};

interface TranslationDataService {
  getTranslationTexts(): TranslationDictionary;

  I18N_KEYS: {
    COMMON: {
      LEGAL_TEXT: string;
      LEGAL_TEXT2: string;
      VERIFIED_TOOLTIP: string;
    };
    FILTER: {
      HEADER: string;
      LABEL: string;
      REMOVE_FILTER: string;
      HIDE_ARIA_LABEL: string;
      SHOW_ARIA_LABEL: string;
    };
    HEADER: { TOTAL_REVIEW_COUNT: string };
    IMPORTED: { SHORT: string; LONG: string };
    RATING: {
      BAR_ARIA_LABEL: string;
      STARS_TEXT: string;
      TOTAL_RATING_ARIA_LABEL: string;
    };
    REVIEW: {
      TOOLTIP: { TITLE: string; DATE: string };
      REPLY_FROM: string;
      SHOW_TRANSLATION: string;
      SHOW_ORIGIN: string;
    };
    REVIEW_LIST: {
      LOAD_MORE: string;
      NO_MORE_REVIEWS: string;
      SHOW_LESS: string;
    };
  };
}

interface ReviewMeta {
  date?: string | number | Date;
  replyDate?: string | number | Date;
}

interface RatingOrigin {
  title?: string;
  comment?: string;
  replyContent?: string;
  language?: string;
}

interface RatingMeta {
  date?: string | number | Date;
  replyDate?: string | number | Date;
}

interface RatingDto {
  rating?: number;
  meta?: RatingMeta;
  origin?: RatingOrigin;
  content?: Record<string, RatingOrigin>;
  userName?: string;
  imported?: { imported: boolean };
}

interface ProductReview {
  productId: string;
  ratings?: RatingDto[];
  ratingDistribution?: Record<string, number>;
  ratingPercentageDistribution?: Record<string, number>;
  totalReviews?: number;
  averageRating: number;
}

interface WidgetOptions {
  ratingElement: HTMLElement | null;
  pId: string | null;
  theme: string;
  maxRatingsPerStep: number;
  locale: string | null;
  widgetVersion: string;
  minRating?: number;
  maxRating?: number;
  tenant?: string;
}

interface ReviewConfig {
  ratingElement: HTMLElement | null;
  pId: string | null;
  shopName: string;
  theme: string;
  widgetVersion: string;
  dateFormat: string;
  dateFormatOptions: Intl.DateTimeFormatOptions;
  minRating: number;
  maxRating: number;
  maxRatingsPerStep: number;
  tenant: string;
  locale: string;
  language: string;
  visibleRatings: number;
}

interface TranslationService {
  t: (key: string) => string;
  I18N_KEYS: {
    FILTER: {
      HIDE_ARIA_LABEL: string;
      SHOW_ARIA_LABEL: string;
    };
  };
}

interface ReviewVisibilityConfig {
  visibleRatings: number;
  maxRatingsPerStep: number;
}

class ETrustedReviewWidgetOptions {
  private ratingElement: HTMLElement | null;
  private pId: string | null;
  private theme: string;
  private maxRatingsPerStep: number;
  private locale: string | null;
  private widgetVersion: string;

  constructor() {
    this.ratingElement = document.querySelector("etrusted-widget-pda");
    if (!this.ratingElement) return;
    this.pId = this.ratingElement.getAttribute("data-sku");
    this.theme = "light";
    this.maxRatingsPerStep = 3;
    this.locale =
      document
        .querySelector("etrusted-widget-pda")
        ?.getAttribute("data-locale") ?? null;
    this.widgetVersion = "1.18.18";
  }

  // falls du später dynamisch Optionen laden/ändern willst
  toObject() {
    return {
      theme: this.theme,
      maxRatingsPerStep: this.maxRatingsPerStep,
      locale: this.locale,
      widgetVersion: this.widgetVersion,
      ratingElement: this.ratingElement,
      pId: this.pId,
    };
  }
}

class ETrustedReviewDataService {
  getProductReview(): ProductReview | null {
    const dataEl = document.querySelector(".reviewdata");
    if (!dataEl) return null;
    const data = dataEl.textContent;
    if (!data) return null;
    return JSON.parse(data);
  }

  sortRatings(productReview: ProductReview | null): ProductReview | null {
    if (!productReview || !Array.isArray(productReview.ratings)) {
      return null;
    }

    productReview.ratings.sort((a, b) => {
      const dateA = new Date(
        (a?.meta as ReviewMeta | undefined)?.date ?? 0,
      ).getTime();
      const dateB = new Date(
        (b?.meta as ReviewMeta | undefined)?.date ?? 0,
      ).getTime();
      return dateB - dateA;
    });
    return productReview;
  }
}

class ETrustedReviewWidgetController {
  private config: ReviewConfig;
  private i18n: ETrustedTranslationService | null = null;
  private renderer: ETrustedReviewRenderer | null = null;

  constructor() {
    const options = new ETrustedReviewWidgetOptions().toObject();

    const eTrustedReviewConfigBuilder = new ETrustedReviewConfigBuilder();
    this.config = eTrustedReviewConfigBuilder.initConfig(options);
  }

  inititialize() {
    const translationDataService = new ETrustedTranslationDataService();
    this.i18n = new ETrustedTranslationService(
      translationDataService,
      this.config.language,
    );
    this.renderer = new ETrustedReviewRenderer(this.i18n, this.config);

    const eTrustedDataProvider = new ETrustedReviewDataService();
    let productReview: ProductReview | null =
      eTrustedDataProvider.getProductReview();
    productReview = eTrustedDataProvider.sortRatings(productReview);

    if (!productReview) return;

    const productReviewListWidget =
      this.renderer.renderProductReviewListWidget(productReview);
    const shadowRootForWidget = this.attachShadowRootWithHtml(
      document,
      `etrusted-widget-${this.config.pId}`,
      productReviewListWidget,
      "open",
    );

    const productReviewAsHtml =
      this.renderer.renderProductReview(productReview);
    const shadowRootForReviews = this.attachShadowRootWithHtml(
      shadowRootForWidget,
      `etrusted-product-review-list-widget-${this.config.pId}`,
      productReviewAsHtml,
      "open",
    );

    if (!shadowRootForReviews) return;

    const reviewVisibilityManager = new ETrustedReviewVisibilityManager(
      shadowRootForReviews,
      this.config,
    );

    const eTrustedToggleManager = new ETrustedToggleManager(
      shadowRootForReviews,
      reviewVisibilityManager,
      this.i18n,
      this.config,
    );
    eTrustedToggleManager.initToggles();

    reviewVisibilityManager.makeReviewsVisible(false);
  }

  attachShadowRootWithHtml(
    rootElement: Document | ShadowRoot | HTMLElement | null,
    hostId: string,
    html: string,
    mode: ShadowRootMode = "open",
  ): ShadowRoot | null {
    if (!rootElement) {
      return null;
    }

    const host = (rootElement as Document).getElementById
      ? (rootElement as Document).getElementById(hostId)
      : (rootElement as ShadowRoot | HTMLElement).querySelector(`#${hostId}`);

    if (!host) {
      return null;
    }

    const shadowRoot =
      (host as HTMLElement).shadowRoot ||
      (host as HTMLElement).attachShadow({ mode });
    if (shadowRoot.innerHTML !== html) {
      shadowRoot.innerHTML = html;
    }

    return shadowRoot;
  }
}

class ETrustedReviewConfigBuilder {
  initConfig(options: Partial<WidgetOptions> = {}): ReviewConfig {
    const ratingElement = options.ratingElement;
    const pId = options.pId;
    const theme = options.theme ?? "light";
    const widgetVersion = options.widgetVersion ?? "1.18.17";
    const dateFormat = "dd.MM.yyyy";
    const dateFormatOptions: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    };

    const minRating = Number(options.minRating ?? 0);
    const maxRating = Number(options.maxRating ?? 5);
    const maxRatingsPerStep = Number(options.maxRatingsPerStep ?? 3);

    const tenant = options.tenant ?? "ssh_de";
    const locale = options.locale ?? "de-DE";
    const language = locale.split("-")[0] ?? "de";

    return <ReviewConfig>{
      shopName: "Schäfer Shop",
      ratingElement,
      pId,
      theme,
      widgetVersion,
      dateFormat,
      dateFormatOptions,
      minRating,
      maxRating,
      maxRatingsPerStep,
      tenant,
      locale,
      language,
      visibleRatings: 0,
    };
  }
}

class ETrustedTranslationService {
  public I18N_KEYS: TranslationDataService["I18N_KEYS"];
  private translations: TranslationDictionary;
  private lang: string;
  public i18nHelper: ETrustedI18nHelper;

  constructor(
    translationDataService: ETrustedTranslationDataService,
    lang: string = "en",
  ) {
    this.I18N_KEYS = translationDataService.I18N_KEYS;
    this.translations = translationDataService.getTranslationTexts() ?? {};
    this.lang = lang || "en";

    this.i18nHelper = new ETrustedI18nHelper(this.lang);
  }

  translate(
    key: string,
    parameters: Record<string, string> = {},
    lang?: string,
  ) {
    lang = lang || this.lang;
    const text = (this.translations[lang]?.[key] as string | undefined) || key;
    return this.replacePlaceholders(text, parameters);
  }

  replacePlaceholders(text: string, parameters: Record<string, string> = {}) {
    // Parameter ersetzen: {name} -> "Vorname"
    for (const [param, value] of Object.entries(parameters)) {
      text = text.replace(new RegExp(`{{${param}}}`, "g"), value);
    }

    return text;
  }

  t(key: string, parameters?: Record<string, string>, lang?: string) {
    return this.translate(key, parameters, lang);
  }

  translatePlural(
    key: string,
    count: number,
    parameters: Record<string, string> = {},
    lang?: string,
  ): string {
    const activeLang = lang || this.lang;
    const pluralKey = this.i18nHelper.getPluralKey(activeLang, count);
    const entry = this.translations[activeLang]?.[key];

    let text: string = key;

    if (entry !== undefined) {
      if (typeof entry === "string") {
        text = entry;
      } else if (typeof entry === "object" && entry !== null) {
        const safeKey = typeof pluralKey === "string" ? pluralKey : "other";
        text = (entry as Record<string, string>)[safeKey] ?? key;
      }
    }

    return this.replacePlaceholders(text, parameters);
  }

  tp(
    key: string,
    count: number,
    parameters?: Record<string, string>,
    lang?: string,
  ) {
    return this.translatePlural(key, count, parameters, lang);
  }

  // Optional: change language dynamically
  changeLanguage(lang: string) {
    if (this.translations[lang]) {
      this.lang = lang;
    }
  }
}

class ETrustedReviewRenderer {
  private i18n: ETrustedTranslationService;
  private config: ReviewConfig;

  constructor(i18n: ETrustedTranslationService, config: ReviewConfig) {
    this.i18n = i18n;
    this.config = config;
  }

  renderProductReviewListWidget(productReview: ProductReview): string {
    return `
      <etrusted-product-review-list-widget
          data-sku="${productReview.productId}"
          data-theme="${this.config.theme}"
          data-font-style="default"
          id="etrusted-product-review-list-widget-${productReview.productId}">
      </etrusted-product-review-list-widget>
    `;
  }

  renderProductReview(productReview: ProductReview): string {
    return `
      <style>
        @import url("https://integrations.etrusted.com/applications/product-review-list-widget/${this.config.widgetVersion}/index.css");
        :host(etrusted-product-review-list-widget), :host(etrusted-star-extension) {
            --accent-color: #FCD300;
        }
        .review-list .footer .action {
            color: #2C689F;
            font-weight: 700;
            font-size: .75rem;
            border: none;
            background: none;
            padding: 0;
            cursor: pointer;
        }
        .review-list .footer .action.load-more {
            background: #28292E;
            border-color: #28292E;
            color: white;
            margin-top: 14px;
            font-size: 14px;
            padding: 12px;
        }
        .review-list .review .footer {
            justify-content: flex-start;
            align-items: flex-start;
        }
        etrusted-review-filter .review-filter button {
            font-size: 1em;
        }
        etrusted-review-filter .filter-list li {
            cursor: pointer;
        }
        etrusted-review-element .review .body .reply {
            box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.02), 0 8px 12px 0 rgba(0, 0, 0, 0.04);
        }
        etrusted-review-element .review .body .reply .marker {
            border-left: 3px solid #2C689F;
        }
      </style>
      <div class="product-review-list-widget">
        <div class="col-40">
          ${this.renderRatingDistribution(productReview)}
        </div>
        <div class="col-60">
          <div class="bar">
            <div class="bar-action">
              ${this.renderRatingFilter(productReview)}
            </div>
            <div class="bar-content"></div>
          </div>
          ${this.renderRatingList(productReview)}
        </div>
      </div>
      <style>
        etrusted-widget {display: block;}
        .product-review-list-widget {background-color: var(--color-bg-highlight);}
      </style>
    `;
  }

  renderRatingStars(fullStars: number) {
    fullStars = Number(fullStars);
    const emptyStars = Number(this.config.maxRating) - fullStars;
    const baseUrl = `https://integrations.etrusted.com/applications/product-review-list-widget/${this.config.widgetVersion}`;

    return `
      <etrusted-rl-star-rating baseurl="${baseUrl}" rating="${fullStars}" theme="${this.config.theme}">
          <div class="star-rating">
              ${ETrustedCommonUtils.repeat(`<div class="filled"></div>`, fullStars, "\n")}
              ${
                emptyStars <= 0
                  ? ""
                  : `
                  <div class="star-rating__fraction">
                    <div class="star"><div class="empty"></div></div>
                    <div class="star" style="width: 0;"><div class="filled"></div></div>
                  </div>
                  ${ETrustedCommonUtils.repeat(`<div class="empty"></div>`, emptyStars - 1, "\n")}
                  `
              }
          </div>
      </etrusted-rl-star-rating>
    `;
  }

  renderRatingElement(ratingDto: RatingDto): string {
    const fullStars = Number(
      ratingDto?.rating != null ? ratingDto.rating : this.config.minRating,
    );

    // dates
    const meta = ratingDto?.meta ?? {};
    const ratingDate = meta.date
      ? ETrustedCommonUtils.formatDate(
          meta.date,
          this.config.dateFormatOptions,
          this.config.locale,
        )
      : "";
    const replyDate = meta.replyDate
      ? ETrustedCommonUtils.formatDate(
          meta.replyDate,
          this.config.dateFormatOptions,
          this.config.locale,
        )
      : ratingDate;

    const origin = ratingDto?.origin ?? {};
    const translationContent =
      ratingDto?.content?.[this.config?.language] ?? origin;

    let ratingTitle;
    let ratingComment;
    let ratingReplyContent;

    const originTitle = ETrustedCommonUtils.sanitizeTextToHtml(origin.title);
    const originComment = ETrustedCommonUtils.sanitizeTextToHtml(
      origin.comment,
    );
    const originReplyContent = ETrustedCommonUtils.sanitizeTextToHtml(
      origin.replyContent,
    );

    let translationTitle = ETrustedCommonUtils.sanitizeTextToHtml(
      translationContent.title,
    );
    let translationComment = ETrustedCommonUtils.sanitizeTextToHtml(
      translationContent.comment,
    );
    let translationReplyContent = ETrustedCommonUtils.sanitizeTextToHtml(
      translationContent.replyContent,
    );

    const isTranslated = Boolean(origin.language !== this.config.language);

    if (isTranslated) {
      ratingTitle = originTitle;
      ratingComment = originComment;
      ratingReplyContent = originReplyContent;
    } else {
      ratingTitle = translationTitle;
      ratingComment = translationComment;
      ratingReplyContent = translationReplyContent;
    }

    ratingTitle = ETrustedCommonUtils.defaultIfEmpty(ratingTitle, "");
    ratingComment = ETrustedCommonUtils.defaultIfEmpty(ratingComment, "");
    ratingReplyContent = ETrustedCommonUtils.defaultIfEmpty(
      ratingReplyContent,
      "",
    );

    translationTitle = ETrustedCommonUtils.defaultIfEmpty(translationTitle, "");
    translationComment = ETrustedCommonUtils.defaultIfEmpty(
      translationComment,
      "",
    );
    translationReplyContent = ETrustedCommonUtils.defaultIfEmpty(
      translationReplyContent,
      "",
    );

    const userName = ratingDto?.userName ?? "";

    const hasAnyContent = Boolean(
      ratingTitle || ratingComment || ratingReplyContent,
    );

    return `
                <etrusted-review-element theme="${this.config.theme}" tabindex="-1" style="display:none;">
                    <div class="review">
                        <div class="header">
                            <div class="row row--space-between">
                                ${this.renderRatingStars(fullStars)}
                                ${
                                  ratingDto?.imported &&
                                  ratingDto.imported.imported
                                    ? `<div class="imported-flag">${this.i18n.t(this.i18n.I18N_KEYS.IMPORTED.SHORT)}</div>`
                                    : `<etrusted-rc-tooltip
                                            content="${ETrustedCommonUtils.excapeHtml(this.i18n.t(this.i18n.I18N_KEYS.COMMON.VERIFIED_TOOLTIP, { shopName: this.config.shopName, dataKeyNav: "" }))}"
                                            orientation="left" data-key-nav="${ETrustedNavigationKeyGenerator.getDataKeyNav()}" tabindex="2">
                                        <div class="verified-icon"></div>
                                        <div class="tooltip-wrapper left" style="left: -276px; display: none;">
                                            <div class="tooltip left">
                                                <span tabindex="-1">${this.i18n.t(this.i18n.I18N_KEYS.COMMON.VERIFIED_TOOLTIP, { shopName: this.config.shopName, dataKeyNav: ETrustedNavigationKeyGenerator.getDataKeyNav().toString() })}</span>
                                            </div>
                                        </div>
                                        </etrusted-rc-tooltip>
                                    `
                                }
                            </div>
                            <div class="row">
                                <etrusted-rc-tooltip content="${this.i18n.t(this.i18n.I18N_KEYS.REVIEW.TOOLTIP.DATE, { date: ratingDate })}">
                                    <span>${!userName ? ratingDate : this.i18n.t(this.i18n.I18N_KEYS.REVIEW.TOOLTIP.TITLE, { date: ratingDate, username: userName })}</span>
                                    <div class="tooltip-wrapper center" style="left: -65.9062px; display: none;">
                                      <div class="tooltip">
                                        <span>${this.i18n.t(this.i18n.I18N_KEYS.REVIEW.TOOLTIP.DATE, { date: ratingDate })}</span>
                                      </div>
                                    </div>
                                </etrusted-rc-tooltip>
                            </div>
                        </div>
                        ${
                          !hasAnyContent
                            ? ""
                            : `
                            <div class="body">
                                ${!ratingTitle ? "" : `<div class="title origin">${ratingTitle}</div>`}
                                ${!translationTitle ? "" : `<div class="title translation" style="display:none;">${translationTitle}</div>`}
                                ${!ratingComment ? "" : `<div class="comment origin">${ratingComment}</div>`}
                                ${!translationComment ? "" : `<div class="comment translation" style="display:none;">${translationComment}</div>`}
                                ${
                                  !ratingReplyContent
                                    ? ""
                                    : `
                                    <div class="reply">
                                        <div class="icon"></div>
                                        <div class="title">${this.i18n.t(this.i18n.I18N_KEYS.REVIEW.REPLY_FROM, { replyFrom: this.config.shopName })}</div>
                                        ${!replyDate?.toString().trim() ? "" : `<div class="date">${replyDate}</div>`}
                                        <div class="marker"></div>
                                        <div class="comment origin">${ratingReplyContent}</div>
                                        ${!translationReplyContent ? "" : `<div class="comment translation" style="display:none;">${translationReplyContent}</div>`}
                                    </div>
                                    `
                                }
                          </div>
                          ${
                            !isTranslated
                              ? ""
                              : `
                              <div class="footer">
                                  <button class="action show-translation cta cta-tertiary" style=""><span>${this.i18n.t(this.i18n.I18N_KEYS.REVIEW.SHOW_TRANSLATION, { language: this.i18n.i18nHelper.getLanguageName(this.config?.language) ?? "" })}</span></button>
                                  <button class="action show-origin cta cta-tertiary" style="display: none;"><span>${this.i18n.t(this.i18n.I18N_KEYS.REVIEW.SHOW_ORIGIN, { language: this.i18n.i18nHelper.getLanguageName(origin?.language ?? "") ?? "" })}</span></button>
                              </div>`
                          }`
                        }
                    </div>
                </etrusted-review-element>
            `;
  }

  renderRatingList(productReview: ProductReview) {
    const ratings = productReview?.ratings ?? [];
    const renderedRatingElements = ratings
      .map((rating) => this.renderRatingElement(rating))
      .join("\n");

    return `
      <etrusted-review-list>
          <div class="review-list">
              <div class="body">
                  ${renderedRatingElements}
              </div>
              <div class="footer">
                  <span class="action load-more cta cta-tertiary" tabindex="2" data-key-nav="${ETrustedNavigationKeyGenerator.getDataKeyNav()}" style="display: none;">${this.i18n.t(this.i18n.I18N_KEYS.REVIEW_LIST.LOAD_MORE)}</span>
                  <span style="display: none;">${this.i18n.t(this.i18n.I18N_KEYS.REVIEW_LIST.NO_MORE_REVIEWS)}<button class="action show-less" tabindex="2" data-key-nav="${ETrustedNavigationKeyGenerator.getDataKeyNav()}">${this.i18n.t(this.i18n.I18N_KEYS.REVIEW_LIST.SHOW_LESS)}</button></span>
              </div>
          </div>
      </etrusted-review-list>
    `;
  }

  renderRatingFilterRow(
    fullStars: string,
    distribution: Record<string, number>,
  ) {
    return `
      <li data-filter-value="${fullStars}" role="checkbox" aria-label="${fullStars} Sterne" aria-checked="false" data-key-nav="${ETrustedNavigationKeyGenerator.getDataKeyNav()}" tabindex="1">
          ${this.renderRatingStars(Number(fullStars))}
          <span>(${Number(Math.round(distribution[fullStars] ?? 0)).toString()})</span>
      </li>
    `;
  }

  renderRatingFilter(productReview: ProductReview) {
    const distribution = productReview.ratingDistribution || {};
    const keys = Object.keys(distribution).sort().reverse();

    return `
      <etrusted-review-filter theme="${this.config.theme}" data-key-nav="${ETrustedNavigationKeyGenerator.getDataKeyNav()}" tabindex="-1">
          <div class="review-filter closed" data-key-nav="${ETrustedNavigationKeyGenerator.getDataKeyNav()}" tabindex="-1">
              <button tabindex="2" contenteditable="false">${this.i18n.t(this.i18n.I18N_KEYS.FILTER.LABEL)} <span class="icon" aria-label="${this.i18n.t(this.i18n.I18N_KEYS.FILTER.SHOW_ARIA_LABEL)}"></span></button>
              <div class="filter">
                  <span>${this.i18n.t(this.i18n.I18N_KEYS.FILTER.HEADER)}</span>
                  <ol class="filter-list">
                      ${keys.map((key) => this.renderRatingFilterRow(key, distribution)).join("\n")}
              </ol>
              <a data-key-nav="${ETrustedNavigationKeyGenerator.getDataKeyNav()}" tabindex="1">${this.i18n.t(this.i18n.I18N_KEYS.FILTER.REMOVE_FILTER)}</a>
            </div>
          </div>
      </etrusted-review-filter>
    `;
  }

  renderRatingDistributionRow(
    fullStars: string,
    distribution: Record<string, number>,
  ) {
    const percentage = distribution[fullStars] ?? 0;
    //const percentageForStyle = String(percentage).replace(/,/g, ".");
    const percentageForDisplay = Number(Math.round(percentage)).toString();

    return `
      <div class="distribution-row" data-filter-value="${fullStars}">
          <span class="rating-index" aria-label="${fullStars}">${fullStars} <span class="rating-star"
                  aria-label="${this.i18n.t(this.i18n.I18N_KEYS.RATING.STARS_TEXT)}"><img class="star empty" alt=""></span></span>
          <div class="bar" aria-label="${this.i18n.t(this.i18n.I18N_KEYS.RATING.BAR_ARIA_LABEL)}">
              <div class="bar-filled" style="width: ${percentageForDisplay}%"></div>
          </div>
          <span class="percent"> ${percentageForDisplay}% </span>
      </div>
    `;
  }

  renderRatingDistributionRows(distribution: Record<string, number>) {
    const keys = Object.keys(distribution).sort().reverse();
    return keys
      .map((key) => this.renderRatingDistributionRow(key, distribution))
      .join("\n");
  }

  /**
   * Builds the distribution HTML for a product review.
   *
   * @param {Object} productReview
   * @returns {string} HTML string with distribution rows
   */
  renderRatingDistribution(productReview: ProductReview) {
    const distribution = productReview.ratingPercentageDistribution || {};
    const totalReviews = Number(productReview.totalReviews ?? 0).toFixed(0);

    return `
      <etrusted-rating-distribution data-key-nav="${ETrustedNavigationKeyGenerator.getDataKeyNav()}" tabindex="-1">
          <div class="rating-distribution">
              <div class="header">
                  <span class="rating">${Number(productReview.averageRating).toFixed(2)}</span>
                  <span class="review-count">${this.i18n.tp(this.i18n.I18N_KEYS.HEADER.TOTAL_REVIEW_COUNT, parseFloat(totalReviews), { count: totalReviews })} </span>
                  <span class="logo" aria-label="${this.config.shopName}"></span>
              </div>
              <div class="body">
                  ${this.renderRatingDistributionRows(distribution)}
              </div>
              <div class="footer">
                  <span>${this.i18n.t(ETrustedTranslationDataService.I18N_KEYS.COMMON.LEGAL_TEXT, { dateKeyNav: ETrustedNavigationKeyGenerator.getDataKeyNav().toString() })}</span>
                  <span>${this.i18n.t(this.i18n.I18N_KEYS.COMMON.LEGAL_TEXT2, { dateKeyNav: ETrustedNavigationKeyGenerator.getDataKeyNav().toString() })}</span>
              </div>
          </div>
      </etrusted-rating-distribution>
    `;
  }
}

class ETrustedCommonUtils {
  static formatDate(
    input: string | number | Date,
    options?: Intl.DateTimeFormatOptions,
    locale: string = "en-US",
  ) {
    const date = input instanceof Date ? input : new Date(input);

    if (!isFinite(date.getTime())) {
      return "";
    }

    return new Intl.DateTimeFormat(locale, options).format(date);
  }

  static repeat(text: string, times: number = 0, separator: string = "") {
    separator = separator || "";
    times = times || 0;
    times = Number(times ?? 0);

    return Array(times).fill(text).join(separator);
  }

  static sanitizeTextToHtml(text: string | null | undefined) {
    return (text ?? "")
      .replaceAll(/<br\s*\/?>/gi, "\n")
      .replaceAll(/\r\n?/g, "\n")
      .replaceAll(/\n/g, "<br>");
  }

  static defaultIfEmpty<T extends string | null | undefined>(
    text: T,
    defaultText: string,
  ): string {
    return text?.toString().trim() ? text : defaultText;
  }

  static excapeHtml(text: string | null | undefined) {
    return (text ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }
}

class ETrustedNavigationKeyGenerator {
  static dataKeyNav = 1;

  static getDataKeyNav() {
    return this.dataKeyNav++;
  }
}

class ETrustedToggleManager {
  private reviewVisibilityManager: ETrustedReviewVisibilityManager;
  private shadowRoot: ShadowRoot;
  private i18n: TranslationService;
  private config: ReviewConfig;
  private _outsideClickListener: ((event: MouseEvent) => void) | null = null;

  constructor(
    shadowRoot: ShadowRoot,
    reviewVisibilityManager: ETrustedReviewVisibilityManager,
    i18n: TranslationService,
    config: ReviewConfig,
  ) {
    this.shadowRoot = shadowRoot;
    this.reviewVisibilityManager = reviewVisibilityManager;
    this.i18n = i18n;
    this.config = config;
  }

  initToggles() {
    this.initFilterViewToggle();
    this.initActiveFilterToggles();
    this.initActiveFilterRemoveToggle();
    this.initTooltipToggles();
    this.initLoadMoreRatingsToggle();
    this.initTranslationToggle();
  }

  initFilterViewToggle() {
    const filters = this.shadowRoot.querySelectorAll(
      "etrusted-review-filter .review-filter",
    );

    filters.forEach((container) => {
      const btn = container.querySelector("button");

      if (!btn) {
        return;
      }

      btn.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          e.stopPropagation();

          let isOpen = container.classList.contains("open");

          if (isOpen) {
            container.classList.remove("open");
            container.classList.add("closed");
          } else {
            container.classList.remove("closed");
            container.classList.add("open");
          }

          isOpen = container.classList.contains("open");
          const icon = btn.querySelector(".icon");

          if (icon) {
            icon.setAttribute(
              "aria-label",
              isOpen
                ? this.i18n.t(this.i18n.I18N_KEYS.FILTER.HIDE_ARIA_LABEL)
                : this.i18n.t(this.i18n.I18N_KEYS.FILTER.SHOW_ARIA_LABEL),
            );
          }

          if (!this._outsideClickListener) {
            this._outsideClickListener = (event) => {
              const icon = btn.querySelector(".icon");
              const isOpen = container.classList.contains("open");
              const path = event.composedPath();

              if (!path.includes(container)) {
                if (isOpen) {
                  container.classList.remove("open");
                  container.classList.add("closed");
                } else {
                  container.classList.remove("closed");
                  container.classList.add("open");
                }

                if (icon) {
                  icon.setAttribute(
                    "aria-label",
                    isOpen
                      ? this.i18n.t(this.i18n.I18N_KEYS.FILTER.HIDE_ARIA_LABEL)
                      : this.i18n.t(this.i18n.I18N_KEYS.FILTER.SHOW_ARIA_LABEL),
                  );
                }

                document.removeEventListener(
                  "click",
                  this._outsideClickListener!,
                  true,
                );
              }
            };
          }

          if (isOpen) {
            document.addEventListener(
              "click",
              this._outsideClickListener,
              true,
            );
          } else {
            document.removeEventListener(
              "click",
              this._outsideClickListener,
              true,
            );
          }
        },
        { capture: false },
      );
    });
  }

  initActiveFilterToggles() {
    const filters = this.shadowRoot.querySelectorAll(".filter .filter-list li");

    filters.forEach((container) => {
      container.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          e.stopPropagation();

          container.classList.toggle("active");
          const isActive = container.classList.contains("active");
          container.setAttribute("aria-checked", isActive ? "true" : "false");

          // ✅ Neue Logik: prüfen ob alle aktiv sind
          const allActive = Array.from(filters).every((f) =>
            f.classList.contains("active"),
          );
          if (allActive) {
            // alle wieder deaktivieren
            filters.forEach((f) => {
              f.classList.remove("active");
              f.setAttribute("aria-checked", "false");
            });
          }

          this.reviewVisibilityManager.makeReviewsVisible(true);
        },
        { capture: false },
      );
    });
  }

  initActiveFilterRemoveToggle() {
    const removeLink = this.shadowRoot.querySelector(".filter a");
    if (!removeLink) {
      return;
    }

    removeLink.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      // alle Filter zurücksetzen
      const filters = this.shadowRoot.querySelectorAll(
        ".filter .filter-list li",
      );
      filters.forEach((f) => {
        f.classList.remove("active");
        f.setAttribute("aria-checked", "false");
      });

      // Reviews neu anzeigen
      this.reviewVisibilityManager.makeReviewsVisible(true);
    });
  }

  toggleTranslation(
    buttonToShow: HTMLElement | null,
    elementsToShow: NodeListOf<HTMLElement>,
    buttonToHide: HTMLElement | null,
    elementsToHide: NodeListOf<HTMLElement>,
  ) {
    if (buttonToHide) {
      buttonToHide.addEventListener("click", () => {
        // Zeige Übersetzung, verstecke Original
        elementsToHide.forEach((element) => (element.style.display = "none"));
        elementsToShow.forEach((element) =>
          element.style.removeProperty("display"),
        );
        if (buttonToHide) {
          buttonToHide.style.display = "none";
        }
        if (buttonToShow) {
          buttonToShow.style.removeProperty("display");
        }
      });
    }
  }

  initTranslationToggle() {
    const reviews = this.shadowRoot.querySelectorAll("etrusted-review-element");

    if (reviews.length === 0) return;

    reviews.forEach((container) => {
      const showTranslationBtn = container.querySelector<HTMLElement>(
        ".button.action.show-translation, .action.show-translation, button.action.show-translation",
      );
      const showOriginBtn = container.querySelector<HTMLElement>(
        ".button.action.show-origin, .action.show-origin, button.action.show-origin",
      );

      if (!showTranslationBtn && !showOriginBtn) {
        return;
      }

      const originElements = container.querySelectorAll<HTMLElement>(
        ".title.origin, .comment.origin",
      );
      const translationElements = container.querySelectorAll<HTMLElement>(
        ".title.translation, .comment.translation",
      );

      this.toggleTranslation(
        showOriginBtn,
        translationElements,
        showTranslationBtn,
        originElements,
      );
      this.toggleTranslation(
        showTranslationBtn,
        originElements,
        showOriginBtn,
        translationElements,
      );
    });
  }

  initTooltipToggles() {
    const filters = this.shadowRoot.querySelectorAll("etrusted-rc-tooltip");

    filters.forEach((container) => {
      const wrapper = container.querySelector<HTMLElement>(".tooltip-wrapper");

      if (!wrapper) {
        return;
      }

      container.addEventListener("mouseenter", () => {
        wrapper.style.display = "block";
      });

      container.addEventListener("mouseleave", () => {
        wrapper.style.display = "none";
      });
    });
  }

  initLoadMoreRatingsToggle() {
    const loadMoreBtn = this.shadowRoot.querySelector<HTMLElement>(
      "etrusted-review-list .footer .load-more",
    );
    const noMoreSpan = this.shadowRoot.querySelector<HTMLElement>(
      "etrusted-review-list .footer span:nth-child(2)",
    );

    const reviews = this.shadowRoot.querySelectorAll("etrusted-review-element");

    if (reviews.length === 0) return;

    loadMoreBtn?.addEventListener("click", () => {
      this.reviewVisibilityManager.makeReviewsVisible(false);
    });

    noMoreSpan?.addEventListener("click", () => {
      this.reviewVisibilityManager.makeReviewsVisible(true);
    });
  }
}

class ETrustedReviewVisibilityManager {
  private config: ReviewVisibilityConfig;
  private shadowRoot: ShadowRoot;

  constructor(shadowRoot: ShadowRoot, config = {}) {
    this.shadowRoot = shadowRoot;
    this.config = {
      visibleRatings: 0,
      maxRatingsPerStep: 5,
      ...config,
    };
  }

  getFilteredReviews(reviews: NodeListOf<HTMLElement>): HTMLElement[] {
    const filters = this.shadowRoot.querySelectorAll(
      ".filter .filter-list .active",
    );
    const filterValues = Array.from(filters).map((el) =>
      el.getAttribute("data-filter-value"),
    );

    if (!filterValues || filterValues.length === 0) {
      return Array.from(reviews);
    }

    const reviewsToShow: HTMLElement[] = [];

    reviews.forEach((container) => {
      const ratingElements = container.querySelectorAll(
        "etrusted-rl-star-rating",
      );
      const rating = Array.from(ratingElements)
        .map((el) => el.getAttribute("rating"))
        .pop();

      if (rating && filterValues.includes(rating)) {
        reviewsToShow.push(container);
      }
    });

    return reviewsToShow;
  }

  makeReviewsVisible(reinit: boolean | undefined) {
    reinit = reinit || false;

    if (reinit) {
      this.config.visibleRatings = 0;
    }

    const reviews: NodeListOf<HTMLElement> = this.shadowRoot.querySelectorAll(
      "etrusted-review-list etrusted-review-element",
    );

    if (reviews.length === 0) return;

    const reviewsToShow = this.getFilteredReviews(reviews);

    reviews.forEach((review) => {
      review.style.display = "none";
    });

    const nextVisible = Math.min(
      this.config.visibleRatings + this.config.maxRatingsPerStep,
      reviewsToShow.length,
    );
    this.config.visibleRatings = nextVisible;

    reviewsToShow.forEach((review, i) => {
      if (i < nextVisible) {
        review.style.removeProperty("display");
      } else {
        review.style.display = "none";
      }
    });

    const loadMoreBtn: HTMLElement | null = this.shadowRoot.querySelector(
      "etrusted-review-list .footer .load-more",
    );
    const noMoreSpan: HTMLElement | null = this.shadowRoot.querySelector(
      "etrusted-review-list .footer span:nth-child(2)",
    );

    if (reviewsToShow.length <= this.config.maxRatingsPerStep) {
      if (loadMoreBtn) loadMoreBtn.style.display = "none";
      if (noMoreSpan) noMoreSpan.style.display = "none";
    } else {
      if (this.config.visibleRatings >= reviewsToShow.length) {
        this.config.visibleRatings = reviewsToShow.length;
        if (loadMoreBtn) loadMoreBtn.style.display = "none";
        if (noMoreSpan) noMoreSpan.style.removeProperty("display");
      }

      if (this.config.visibleRatings < reviewsToShow.length) {
        if (loadMoreBtn) loadMoreBtn.style.removeProperty("display");
        if (noMoreSpan) noMoreSpan.style.display = "none";
      }
    }
  }
}

class ETrustedI18nHelper {
  private lang: string;
  private pluralRulesCache: Map<string, Intl.PluralRules>;
  constructor(lang = "en") {
    this.lang = lang;
    this.pluralRulesCache = new Map();
  }

  getPluralKey(locale: string, count: number) {
    // Sonderfall: 0 immer explizit abfangen
    if (count === 0) {
      return "zero";
    }

    // PluralRules für locale aus Cache holen oder neu erstellen
    if (!this.pluralRulesCache.has(locale)) {
      this.pluralRulesCache.set(
        locale,
        new Intl.PluralRules(locale, { type: "cardinal" }),
      );
    }

    const pluralRules = this.pluralRulesCache.get(locale);
    return pluralRules?.select(count);
  }

  getLanguageName(code: string) {
    const displayNames = new Intl.DisplayNames([this.lang], {
      type: "language",
    });
    return displayNames.of(code);
  }
}

class ETrustedTranslationDataService {
  I18N_KEYS: {
    COMMON: {
      LEGAL_TEXT: string;
      LEGAL_TEXT2: string;
      VERIFIED_TOOLTIP: string;
    };
    FILTER: {
      HEADER: string;
      LABEL: string;
      REMOVE_FILTER: string;
      HIDE_ARIA_LABEL: string;
      SHOW_ARIA_LABEL: string;
    };
    HEADER: { TOTAL_REVIEW_COUNT: string };
    IMPORTED: { SHORT: string; LONG: string };
    RATING: {
      BAR_ARIA_LABEL: string;
      STARS_TEXT: string;
      TOTAL_RATING_ARIA_LABEL: string;
    };
    REVIEW: {
      TOOLTIP: { TITLE: string; DATE: string };
      REPLY_FROM: string;
      SHOW_TRANSLATION: string;
      SHOW_ORIGIN: string;
    };
    REVIEW_LIST: {
      LOAD_MORE: string;
      NO_MORE_REVIEWS: string;
      SHOW_LESS: string;
    };
  };
  constructor() {
    this.I18N_KEYS = ETrustedTranslationDataService.I18N_KEYS;
  }

  static I18N_KEYS = {
    COMMON: {
      LEGAL_TEXT: "product.reviews.pda.common.legalText",
      LEGAL_TEXT2: "product.reviews.pda.common.legalText2",
      VERIFIED_TOOLTIP: "product.reviews.pda.common.verifiedTooltip",
    },
    FILTER: {
      HEADER: "product.reviews.pda.filter.header",
      LABEL: "product.reviews.pda.filter.label",
      REMOVE_FILTER: "product.reviews.pda.filter.removeFilter",
      HIDE_ARIA_LABEL: "product.reviews.pda.filter.hideAriaLabel",
      SHOW_ARIA_LABEL: "product.reviews.pda.filter.showAriaLabel",
    },
    HEADER: {
      TOTAL_REVIEW_COUNT: "product.reviews.pda.header.totalReviewCount",
    },
    IMPORTED: {
      SHORT: "product.reviews.pda.imported.short",
      LONG: "product.reviews.pda.imported.long",
    },
    RATING: {
      BAR_ARIA_LABEL: "product.reviews.pda.rating.barAriaLabel",
      STARS_TEXT: "product.reviews.pda.rating.starsText",
      TOTAL_RATING_ARIA_LABEL:
        "product.reviews.pda.rating.totalRatingAriaLabel",
    },
    REVIEW: {
      TOOLTIP: {
        TITLE: "product.reviews.pda.review.tooltip.title",
        DATE: "product.reviews.pda.review.dateTooltip",
      },
      REPLY_FROM: "product.reviews.pda.review.replyFrom",
      SHOW_TRANSLATION: "product.reviews.pda.review.showTranslation",
      SHOW_ORIGIN: "product.reviews.pda.review.showOrigin",
    },
    REVIEW_LIST: {
      LOAD_MORE: "product.reviews.pda.reviewList.loadMore",
      NO_MORE_REVIEWS: "product.reviews.pda.reviewList.noMoreReviews",
      SHOW_LESS: "product.reviews.pda.reviewList.showLess",
    },
  };

  getTranslationTexts() {
    /*
     * Sources:
     * de: https://integrations.etrusted.com/applications/product-review-list-widget/1.18.17/assets/locales/de-DE.json
     * en: https://integrations.etrusted.com/applications/product-review-list-widget/1.18.17/assets/locales/en-GB.json
     * fr: https://integrations.etrusted.com/applications/product-review-list-widget/1.18.17/assets/locales/fr-FR.json
     * nl: https://integrations.etrusted.com/applications/product-review-list-widget/1.18.17/assets/locales/nl-NL.json
     */
    return {
      de: {
        [this.I18N_KEYS.COMMON.LEGAL_TEXT]:
          'Gesammelt unter den Trusted Shops <a target="_blank" href="https://policies.etrusted.com/DE/de/tos.html" data-key-nav="{{dateKeyNav}}" tabindex="1">Nutzungsbedingungen</a>',
        [this.I18N_KEYS.COMMON.LEGAL_TEXT2]:
          '<a target="_blank" href="https://help.etrusted.com/hc/de/articles/23970864566162" data-key-nav="{{dateKeyNav}}" tabindex="1">Über die Echtheit der Trusted Shops Bewertungen.</a>',
        [this.I18N_KEYS.COMMON.VERIFIED_TOOLTIP]:
          '{{shopName}} hat Trusted Shops verwendet, um diese Bewertung zu sammeln. Das bedeutet, dass wir überprüfen können, ob diese Bewertung auf einer echten Kundenerfahrung beruht. <a href="https://help.etrusted.com/hc/de/articles/23970864566162-So-funktionieren-Bewertungen-bei-Trusted-Shops" target="_blank" data-key-nav="{{dataKeyNav}}">Erfahre mehr über die Echtheit von Bewertungen.</a>',

        [this.I18N_KEYS.FILTER.HEADER]: "Bewertungen",
        [this.I18N_KEYS.FILTER.LABEL]: "Filter",
        [this.I18N_KEYS.FILTER.REMOVE_FILTER]: "Filter entfernen",
        [this.I18N_KEYS.FILTER.HIDE_ARIA_LABEL]: "Filteroptionen ausblenden",
        [this.I18N_KEYS.FILTER.SHOW_ARIA_LABEL]: "Filteroptionen anzeigen",

        [this.I18N_KEYS.IMPORTED.SHORT]: "Importiert",
        [this.I18N_KEYS.IMPORTED.LONG]: "Importierte Bewertungen:",
        [this.I18N_KEYS.HEADER.TOTAL_REVIEW_COUNT]: {
          zero: "Dieses Produkt wurde noch nicht bewertet.",
          one: "{{count}} Bewertung",
          other: "{{count}} Bewertungen",
        },

        [this.I18N_KEYS.RATING.BAR_ARIA_LABEL]: "Bar überprüfen",
        [this.I18N_KEYS.RATING.STARS_TEXT]: "Sterne",
        [this.I18N_KEYS.RATING.TOTAL_RATING_ARIA_LABEL]:
          "Sternebewertung von {{rating}} von 5 Sternen",

        [this.I18N_KEYS.REVIEW.TOOLTIP.TITLE]: "{{date}} von {{username}}",
        [this.I18N_KEYS.REVIEW.TOOLTIP.DATE]:
          "Datum der Bewertung: {{date}} <br/>Datum der Erfahrung: {{date}}",
        [this.I18N_KEYS.REVIEW.REPLY_FROM]: "Antwort von {{replyFrom}}",
        [this.I18N_KEYS.REVIEW.SHOW_TRANSLATION]:
          "Automatische Übersetzung in {{language}} anzeigen",
        [this.I18N_KEYS.REVIEW.SHOW_ORIGIN]:
          "Original in {{language}} anzeigen",

        [this.I18N_KEYS.REVIEW_LIST.LOAD_MORE]: "Mehr anzeigen",
        [this.I18N_KEYS.REVIEW_LIST.NO_MORE_REVIEWS]:
          "Keine weiteren Bewertungen.",
        [this.I18N_KEYS.REVIEW_LIST.SHOW_LESS]: "Weniger anzeigen",
      },
      en: {
        [this.I18N_KEYS.COMMON.LEGAL_TEXT]:
          'Collected according to the <a target="_blank" href="https://policies.etrusted.com/GB/en/tos.html" data-key-nav="{{dateKeyNav}}" tabindex="1">terms and conditions</a> of Trusted Shops',
        [this.I18N_KEYS.COMMON.LEGAL_TEXT2]:
          '<a target="_blank" href="https://help.etrusted.com/hc/en-gb/articles/23970864566162" data-key-nav="{{dateKeyNav}}" tabindex="1">About the authenticity of reviews</a>',
        [this.I18N_KEYS.COMMON.VERIFIED_TOOLTIP]:
          '{{shopName}} has used Trusted Shops to collect this customer review. This means that we can verify that this review is based on an actual experience. <a href="https://help.etrusted.com/hc/en-gb/articles/23970864566162-Measures-for-Reviews-Feedback-Verification" target="_blank" data-key-nav="{{dataKeyNav}}">Learn more about the authenticity of customer reviews.</a>',

        [this.I18N_KEYS.FILTER.HEADER]: "Reviews",
        [this.I18N_KEYS.FILTER.LABEL]: "Filter",
        [this.I18N_KEYS.FILTER.REMOVE_FILTER]: "Remove filter",
        [this.I18N_KEYS.FILTER.HIDE_ARIA_LABEL]: "Hide filter options",
        [this.I18N_KEYS.FILTER.SHOW_ARIA_LABEL]: "Show filter options",

        [this.I18N_KEYS.IMPORTED.SHORT]: "Imported",
        [this.I18N_KEYS.IMPORTED.LONG]: "Imported reviews:",
        [this.I18N_KEYS.HEADER.TOTAL_REVIEW_COUNT]: {
          zero: "This product has not yet been rated.",
          one: "{{count}} Review",
          other: "{{count}} Reviews",
        },

        [this.I18N_KEYS.RATING.BAR_ARIA_LABEL]: "Review Bar",
        [this.I18N_KEYS.RATING.STARS_TEXT]: "stars",
        [this.I18N_KEYS.RATING.TOTAL_RATING_ARIA_LABEL]:
          "Star rating of {{rating}} out of 5 stars",

        [this.I18N_KEYS.REVIEW.TOOLTIP.TITLE]: "{{date}} by {{username}}",
        [this.I18N_KEYS.REVIEW.TOOLTIP.DATE]:
          "Date of review: {{date}} <br/>Date of experience: {{date}}",
        [this.I18N_KEYS.REVIEW.REPLY_FROM]: "Answer from {{replyFrom}}",
        [this.I18N_KEYS.REVIEW.SHOW_TRANSLATION]:
          "Show automatic translation in {{language}}",
        [this.I18N_KEYS.REVIEW.SHOW_ORIGIN]: "Show original in {{language}}",

        [this.I18N_KEYS.REVIEW_LIST.LOAD_MORE]: "Load more",
        [this.I18N_KEYS.REVIEW_LIST.NO_MORE_REVIEWS]: "No more reviews.",
        [this.I18N_KEYS.REVIEW_LIST.SHOW_LESS]: "Show less",
      },
      es: {
        [this.I18N_KEYS.COMMON.LEGAL_TEXT]:
          'Recopilado de acuerdo con las <a target="_blank" href="https://policies.etrusted.com/ES/es/tos.html" data-key-nav="{{dateKeyNav}}" tabindex="1">Condiciones de uso</a> de Trusted Shops',
        [this.I18N_KEYS.COMMON.LEGAL_TEXT2]:
          '<a target="_blank" href="https://help.etrusted.com/hc/es/articles/23970864566162" data-key-nav="{{dateKeyNav}}" tabindex="1">Acerca de la autenticidad de las valoraciones de Trusted Shops</a>',
        [this.I18N_KEYS.COMMON.VERIFIED_TOOLTIP]:
          '{{shopName}} ha utilizado Trusted Shops para recopilar esta valoración de cliente. Esto significa que podemos verificar que esta opinión se basa en una experiencia real. <a href="https://help.etrusted.com/hc/es/articles/23970864566162-Medidas-para-la-verificaci%C3%B3n-de-rese%C3%B1as" target="_blank" data-key-nav="{{dataKeyNav}}">Infórmate sobre la autenticidad de las valoraciones de clientes.</a>',

        [this.I18N_KEYS.FILTER.HEADER]: "Valoraciones",
        [this.I18N_KEYS.FILTER.LABEL]: "Filtros",
        [this.I18N_KEYS.FILTER.REMOVE_FILTER]: "Restablecer",
        [this.I18N_KEYS.FILTER.HIDE_ARIA_LABEL]: "Ocultar opciones de filtro.",
        [this.I18N_KEYS.FILTER.SHOW_ARIA_LABEL]: "Mostrar opciones de filtro.",

        [this.I18N_KEYS.IMPORTED.SHORT]: "Importado",
        [this.I18N_KEYS.IMPORTED.LONG]: "Valoraciones importadas:",
        [this.I18N_KEYS.HEADER.TOTAL_REVIEW_COUNT]: {
          zero: "Esta valoración no contiene ningún comentario.",
          one: "{{count}} valoración",
          other: "{{count}} valoraciones",
        },

        [this.I18N_KEYS.RATING.BAR_ARIA_LABEL]: "Bar de reseñas.",
        [this.I18N_KEYS.RATING.STARS_TEXT]: "estrellas",
        [this.I18N_KEYS.RATING.TOTAL_RATING_ARIA_LABEL]:
          "Calificación de {{rating}} de 5 estrellas",

        [this.I18N_KEYS.REVIEW.TOOLTIP.TITLE]: "{{date}} por {{username}}",
        [this.I18N_KEYS.REVIEW.TOOLTIP.DATE]:
          "Fecha de la valoración: {{date}} <br/>Fecha de experiencia: {{date}}",
        [this.I18N_KEYS.REVIEW.REPLY_FROM]: "Respuesta de {{replyFrom}}",
        [this.I18N_KEYS.REVIEW.SHOW_TRANSLATION]:
          "Mostrar traducción automática en {{language}}",
        [this.I18N_KEYS.REVIEW.SHOW_ORIGIN]: "Mostrar original en {{language}}",

        [this.I18N_KEYS.REVIEW_LIST.LOAD_MORE]: "Mostrar más",
        [this.I18N_KEYS.REVIEW_LIST.NO_MORE_REVIEWS]:
          "Ninguna otra valoración.",
        [this.I18N_KEYS.REVIEW_LIST.SHOW_LESS]: "Ver menos",
      },
      fr: {
        [this.I18N_KEYS.COMMON.LEGAL_TEXT]:
          'Rassemblés dans les <a target="_blank" href="https://policies.etrusted.com/FR/fr/tos.html" data-key-nav="{{dateKeyNav}}" tabindex="1">Conditions d’utilisation</a> de Trusted Shops',
        [this.I18N_KEYS.COMMON.LEGAL_TEXT2]:
          '<a target="_blank" href="https://help.etrusted.com/hc/fr/articles/23970864566162" data-key-nav="{{dateKeyNav}}" tabindex="1">À propos de l’authenticité des avis clients Trusted Shops.</a>',
        [this.I18N_KEYS.COMMON.VERIFIED_TOOLTIP]:
          '{{shopName}} a utilisé Trusted Shops pour collecter cet avis client. Cela signifie que nous pouvons vérifier que cet avis est fondé sur une expérience réelle. <a href="https://help.etrusted.com/hc/fr/articles/23970864566162-Pourquoi-les-avis-clients-publi%C3%A9s-par-Trusted-Shops-sont-ils-fiables" target="_blank" data-key-nav="{{dataKeyNav}}">En savoir plus sur l’authenticité des avis clients.</a>',

        [this.I18N_KEYS.FILTER.HEADER]: "Avis",
        [this.I18N_KEYS.FILTER.LABEL]: "Filtres",
        [this.I18N_KEYS.FILTER.REMOVE_FILTER]: "Réinitialisez",
        [this.I18N_KEYS.FILTER.HIDE_ARIA_LABEL]:
          "Masquer les options de filtre",
        [this.I18N_KEYS.FILTER.SHOW_ARIA_LABEL]:
          "Afficher les options de filtre",

        [this.I18N_KEYS.IMPORTED.SHORT]: "Importé",
        [this.I18N_KEYS.IMPORTED.LONG]: "Avis importés:",
        [this.I18N_KEYS.HEADER.TOTAL_REVIEW_COUNT]: {
          zero: "Ce produit n’a pas encore été évalué.",
          one: "{{count}} avis",
          other: "{{count}} avis",
        },

        [this.I18N_KEYS.RATING.BAR_ARIA_LABEL]: "Bar de révision",
        [this.I18N_KEYS.RATING.STARS_TEXT]: "étoiles",
        [this.I18N_KEYS.RATING.TOTAL_RATING_ARIA_LABEL]:
          "Note de {{rating}} sur 5 étoiles",

        [this.I18N_KEYS.REVIEW.TOOLTIP.TITLE]: "{{date}} par le {{username}}",
        [this.I18N_KEYS.REVIEW.TOOLTIP.DATE]:
          "Date de l’avis : {{date}} <br/>Date de l’expérience : {{date}}",
        [this.I18N_KEYS.REVIEW.REPLY_FROM]: "Réponse de {{replyFrom}}",
        [this.I18N_KEYS.REVIEW.SHOW_TRANSLATION]:
          "Afficher la traduction automatique en {{language}}",
        [this.I18N_KEYS.REVIEW.SHOW_ORIGIN]:
          "Afficher l’original en {{language}}",

        [this.I18N_KEYS.REVIEW_LIST.LOAD_MORE]: "Afficher plus",
        [this.I18N_KEYS.REVIEW_LIST.NO_MORE_REVIEWS]: "Aucun autre avis.",
        [this.I18N_KEYS.REVIEW_LIST.SHOW_LESS]: "Voir moins",
      },
      nl: {
        [this.I18N_KEYS.COMMON.LEGAL_TEXT]:
          'Verzameld onder de Trusted Shops <a target="_blank" href="https://policies.etrusted.com/NL/nl/tos.html" data-key-nav="{{dateKeyNav}}" tabindex="1">Gebruiksvoorwaarden</a>',
        [this.I18N_KEYS.COMMON.LEGAL_TEXT2]:
          '<a target="_blank" href="https://help.etrusted.com/hc/nl/articles/23970864566162" data-key-nav="{{dateKeyNav}}" tabindex="1">Over de authenticiteit van beoordelingen van Trusted Shops.</a>',
        [this.I18N_KEYS.COMMON.VERIFIED_TOOLTIP]:
          '{{shopName}} heeft Trusted Shops gebruikt om deze klantenbeoordeling te verzamelen. Dit betekent dat we kunnen verifiëren dat deze beoordeling gebaseerd is op een daadwerkelijke ervaring. <a href="https://help.etrusted.com/hc/nl/articles/23970864566162-Maatregelen-ter-verificatie-van-beoordelingen-feedback" target="_blank" data-key-nav="{{dataKeyNav}}">Leer meer over de authenticiteit van klantenbeoordelingen.</a>',

        [this.I18N_KEYS.FILTER.HEADER]: "Beoordelingen",
        [this.I18N_KEYS.FILTER.LABEL]: "Filter",
        [this.I18N_KEYS.FILTER.REMOVE_FILTER]: "Resetten",
        [this.I18N_KEYS.FILTER.HIDE_ARIA_LABEL]: "Verberg filteropties",
        [this.I18N_KEYS.FILTER.SHOW_ARIA_LABEL]: "Toon filteropties",

        [this.I18N_KEYS.IMPORTED.SHORT]: "Geïmporteerd",
        [this.I18N_KEYS.IMPORTED.LONG]: "Geïmporteerde beoordelingen:",
        [this.I18N_KEYS.HEADER.TOTAL_REVIEW_COUNT]: {
          zero: "Dit product werd nog niet beoordeeld.",
          one: "{{count}} beoordeling",
          other: "{{count}} beoordelingen",
        },

        [this.I18N_KEYS.RATING.BAR_ARIA_LABEL]: "Bar beoordelen",
        [this.I18N_KEYS.RATING.STARS_TEXT]: "sterren",
        [this.I18N_KEYS.RATING.TOTAL_RATING_ARIA_LABEL]:
          "Sterrenbeoordeling van {{rating}} uit 5 sterren",

        [this.I18N_KEYS.REVIEW.TOOLTIP.TITLE]: "{{date}} door {{username}}",
        [this.I18N_KEYS.REVIEW.TOOLTIP.DATE]:
          "Datum van de beoordeling: {{date}} <br/>Datum van de ervaring: {{date}}",
        [this.I18N_KEYS.REVIEW.REPLY_FROM]: "Antwoord van {{replyFrom}}",
        [this.I18N_KEYS.REVIEW.SHOW_TRANSLATION]:
          "Automatische vertaling in {{language}} weergeven",
        [this.I18N_KEYS.REVIEW.SHOW_ORIGIN]:
          "Origineel in {{language}} weergeven",

        [this.I18N_KEYS.REVIEW_LIST.LOAD_MORE]: "Meer tonen",
        [this.I18N_KEYS.REVIEW_LIST.NO_MORE_REVIEWS]:
          "Geen andere beoordelingen.",
        [this.I18N_KEYS.REVIEW_LIST.SHOW_LESS]: "Minder bekijken",
      },
    };
  }
}

const eTrustedRatings = new ETrustedReviewWidgetController();
eTrustedRatings.inititialize();
